import { useEffect, useState } from 'react';
import { Svg } from '../art/icons.js';
import { useStore } from '../lib/store.js';
import { NATIVE } from '../lib/platform.js';
import { hasSubscription, notNow, pushState, recordOffersChoice, refreshPushState, subscribePush } from '../lib/push.js';
import { DISMISS, askScreen, askSettings, askToShow, markAskShown, subscribeToast, type AskAction, type NotificationsAskKind } from '../lib/notifications.js';
import { Sheet } from './ui.js';

/*
 * The notifications screen (Saif, 25 Oct 2026; CLAUDE.md §7.7): ours, in front of the phone's Allow box, with wording
 * that names offers. Turn on opens the phone's box and, on Allow, the server records order updates and offers by push
 * with this wording. Not now (or closing it) records nothing and never opens the phone's box. On a phone that had
 * already allowed notifications, the one-time version asks about offers: "Yes, include offers" or "Just order updates".
 */

const BELL = '<svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z"/><path d="M10 20.5a2.2 2.2 0 0 0 4 0"/></svg>';

export function NotificationsSheet({ mode, place }: { mode: NotificationsAskKind; place: 'home' | 'status' }) {
  const s = useStore();
  const screen = askScreen(mode, askSettings(s.config?.push));
  const [busy, setBusy] = useState(false);
  const run = async (a: AskAction) => {
    if (busy) return;
    setBusy(true);
    if (a.call === 'not_now') {
      s.closeSheet();
      await notNow(mode, place);
      await s.refreshMe().catch(() => null);
      return;
    }
    if (a.call === 'choice') {
      try {
        await recordOffersChoice(a.consent);
        s.toast(a.consent.offers ? 'Offers are on. Turn them off any time in Account.' : 'Just order updates. Change it any time in Account.');
      } catch {
        s.toast('Couldn’t save that. You can choose in Account › Notifications.');
      }
      s.closeSheet();
      await s.refreshMe().catch(() => null);
      return;
    }
    // Turn on: the sheet stays up behind the phone's box; Don't Allow records nothing.
    const r = await subscribePush({ consent: a.consent });
    s.closeSheet();
    const t = subscribeToast(r);
    if (t) s.toast(t);
    await s.refreshMe().catch(() => null);
  };
  return (
    <Sheet onClose={() => void run(DISMISS)} locked={busy}>
      <div className="notifask">
        <span className="notifask-bell"><Svg html={BELL} /></span>
        <h2 className="h2">{screen.title}</h2>
        <p>{screen.text}</p>
        <button type="button" className="yellow" disabled={busy} onClick={() => void run(screen.primary.action)}>{busy && screen.primary.action.call !== 'not_now' ? 'One moment…' : screen.primary.label}</button>
        <button type="button" className={mode === 'offers' ? 'pillbtn notifask-second' : 'link notifask-second'} disabled={busy} onClick={() => void run(screen.secondary.action)}>{screen.secondary.label}</button>
      </div>
    </Sheet>
  );
}

/** Wait for the opening glide (and the first-open welcome) to finish: the ask never lands on top of them. */
function useIntroDone(): boolean {
  const [done, setDone] = useState(() => typeof document === 'undefined' || !document.documentElement.hasAttribute('data-intro'));
  useEffect(() => {
    if (done) return;
    const on = () => setDone(true);
    window.addEventListener('tikkas:intro-done', on);
    return () => window.removeEventListener('tikkas:intro-done', on);
  }, [done]);
  return done;
}

/**
 * Opens the screen when the shared rule says so: on Home right after joining and again N days after a Not now (or the
 * one-time offers version), on the status page after an order. Only with no other sheet or effect showing, once a visit.
 */
export function useNotificationsAsk(place: 'home' | 'status', ready: boolean): void {
  const s = useStore();
  const introDone = useIntroDone();
  const u = s.me?.customer;
  const blocked = !!s.sheet || !!s.fx;
  useEffect(() => {
    if (!ready || !introDone || !u || blocked || !s.config) return;
    let cancelled = false;
    const t = window.setTimeout(async () => {
      const permission = NATIVE ? await refreshPushState() : pushState();
      const subscribed = permission === 'granted' ? await hasSubscription() : false;
      if (cancelled) return;
      const cfg = askSettings(s.config?.push);
      const kind = askToShow({
        enabled: s.config?.push.promptAfterFirstOrder !== false,
        available: NATIVE || !!s.config?.push.configured,
        signedIn: true,
        permission,
        subscribed,
        place,
        // An older server sends no notifications facts: never ask the offers question then.
        offersDecided: u.notifications?.offersDecided ?? true,
        notNowAt: u.notifications?.notNowAt ?? null,
        askAgainDays: cfg.askAgainDays,
        now: new Date(),
      });
      if (!kind || cancelled) return;
      markAskShown();
      s.openSheet({ kind: 'notifications', mode: kind, place });
    }, 700);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [ready, introDone, u?.id, blocked, !!s.config, place]); // eslint-disable-line react-hooks/exhaustive-deps
}
